import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Form, Input, InputNumber, Row, Space, Table, TableProps, Tag, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined } from "@ant-design/icons";
import { BiEdit } from "react-icons/bi";
import { targetStore } from "src/stores/target-store";
import { useShallow } from "zustand/react/shallow";
import { ResponseTarget } from "src/entity/target/target.entity";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import { convertStringToRawNumber, formatNumber } from "src/utils/process-number";
import { omit } from "lodash";
import { profileStore } from "src/stores/profile-store";
import { BsPencilSquare } from "react-icons/bs";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable, canViewTable } from "./PermissionCheck/PermissionCheck";

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: "number" | "text";
  record: ResponseTarget;
  index: number;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode =
    inputType === "number" ? (
      <InputNumber
        className="w-24"
        min={0}
        step={1}
        onKeyDown={(e) => {
          onKeyDownNumberWithFixedDecimal(e, 2);
        }}
        formatter={(val) => formatNumber(val as number, 2)}
      />
    ) : (
      <Input />
    );

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item name={dataIndex} style={{ margin: 0 }}>
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const TargetTable: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const dateMinusFiveYears = new Date();
  dateMinusFiveYears.setFullYear(currentYear);
  const { permissions } = useNavigationMenu();
  // console.log("[Target] permissions : ", permissions);

  const datePlusFiveYears = new Date();
  datePlusFiveYears.setFullYear(currentYear + 9);

  const [form] = Form.useForm();
  const [rangeYear, setRangeYear] = useState<[Dayjs, Dayjs]>([dayjs(dateMinusFiveYears), dayjs(datePlusFiveYears)]);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const profile = profileStore(useShallow((state) => state.profile));

  const getTarget = targetStore(useShallow((state) => state.getTarget));
  const data = targetStore(useShallow((state) => state.data));
  const loading = targetStore(useShallow((state) => state.loading));
  const setDataTarget = targetStore(useShallow((state) => state.setDataTarget));
  const updateTarget = targetStore(useShallow((state) => state.updateTarget));
  const loadingManage = targetStore(useShallow((state) => state.loadingManage));

  // console.log("[Render] data : ", data);

  const canEdit = useMemo(() => {
    return canEditTable(permissions);
  }, [permissions]);

  const isEditing = (record: ResponseTarget) => record._id === editingKey;

  const edit = (record: ResponseTarget) => {
    const configObjectyearForm: Record<string, string | number> = {};
    for (const item of record.years) {
      configObjectyearForm["year-" + item.year] = item.Ton;
    }
    form.setFieldsValue({ ...configObjectyearForm });
    setEditingKey(record._id as string);
  };

  const cancel = () => {
    form.resetFields();
    setEditingKey(null);
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as Record<string, string | number | undefined>;
      // console.log("[Save] key row : ", { key, row });
      const newData = [...data];
      const index = newData.findIndex((item) => key === item._id);

      const newListYears = [...newData[index].years];
      // const newYearSlotLists = [];
      for (const keyYear in row) {
        const editingYear = keyYear.replace("year-", "");
        const value = row[keyYear];

        if (newListYears.find((nly) => nly.year == editingYear))
          newListYears.forEach((yearObject) => {
            if (yearObject.year == editingYear) {
              yearObject.Ton = value ? convertStringToRawNumber(value) : 0;
              yearObject.Kg = value ? +convertStringToRawNumber(value) * 1000 : 0;
            }
          });
        else {
          newListYears.push({
            year: `${editingYear}`,
            Ton: value ? convertStringToRawNumber(value) : 0,
            Kg: value ? +convertStringToRawNumber(value) * 1000 : 0,
          });
        }
      }

      // console.log("After save : ", newListYears);
      if (index > -1) {
        const item = newData[index];
        item.years = newListYears;
        // console.log("Final item after edit : ", item);
        newData.splice(index, 1, {
          ...item,
        });
        setDataTarget(newData);
        setEditingKey(null);
        updateTarget({
          ...omit(item, ["companyId"]),
          companyId: item.companyId?._id as string,
        }).then(() => request());
      }
      cancel();
    } catch (errInfo) {
      // console.log("Validate Failed:", errInfo);
    }
  };
  const request = () => {
    getTarget({ isActive: true });
  };

  useEffect(() => {
    request();
  }, []);

  const arraySelectedYears = useMemo(() => {
    const startYear = rangeYear[0].format("YYYY");
    const endYear = rangeYear[1].format("YYYY");
    const yearArray = Array.from({ length: parseInt(endYear) - parseInt(startYear) + 1 }, (_, index) =>
      (parseInt(startYear) + index).toString(),
    );
    return yearArray;
  }, [rangeYear]);

  const columnsYears: TableProps<ResponseTarget>["columns"] = useMemo(() => {
    return arraySelectedYears.map((year) => {
      return {
        title: year,
        key: year,
        dataIndex: "year-" + year,
        editable: true,
        render: (_, record) => {
          const findYearSlot = record.years.find((item) => item.year == year);
          return <Text className="pr-3">{findYearSlot ? formatNumber(findYearSlot?.Ton as number, 2) : 0}</Text>;
        },
        align: "left",
      };
    });
  }, [arraySelectedYears]);

  // const getIndex = (index: number) => (page - 1) * limit + index;

  const columns: TableProps<ResponseTarget>["columns"] = [
    {
      title: "No",
      key: "no",
      dataIndex: "no",
      render: (text) => <Text>{text}</Text>,
      align: "left",
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render: (_, record) => <Text>{record?.companyId ? record.companyId.companyName : record.alias || ""}</Text>,
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      render: (text) => <Text>{text}</Text>,
    },
    ...columnsYears,

    {
      title: "Action",
      key: "action",
      dataIndex: "action",
      align: "center",
      render: (_: any, record: ResponseTarget) => {
        const editable = isEditing(record);

        return editable ? (
          <Space direction={"vertical"}>
            <Button type={"primary"} className="min-w-[3.9rem]" size={"small"} onClick={async () => save(record._id)}>
              Save
            </Button>
            <Button type={"primary"} className="bg-transparent" size={"small"} ghost onClick={() => cancel()}>
              Cancel
            </Button>
          </Space>
        ) : (
          <Space size="middle">
            <Button
              className="border-transparent bg-transparent"
              size={"small"}
              onClick={() => edit(record)}
              disabled={!record.canEdit || editingKey !== null}
            >
              <BsPencilSquare size={18} />
            </Button>
          </Space>
        );
      },
    },
  ];
  const mergedColumns: TableProps<ResponseTarget>["columns"] = columns.map((col) => {
    if (!("editable" in col) || !col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: ResponseTarget) => ({
        record,
        inputType: "dataIndex" in col && (col.dataIndex as string).includes("year-") ? "number" : "text",
        dataIndex: "dataIndex" in col ? col?.dataIndex : "",
        title: col.title as string,
        editing: isEditing(record),
      }),
    };
  });

  const canView = useMemo(() => {
    return canViewTable(permissions);
  }, [permissions]);

  return (
    <div className="w-full">
      <Row>
        <RangePicker
          picker="year"
          id={{
            start: "startInput",
            end: "endInput",
          }}
          allowClear={false}
          value={rangeYear}
          onChange={(e) => {
            setRangeYear(e as [Dayjs, Dayjs]);
          }}
        />
      </Row>
      <div className="mt-4">
        <Form form={form} component={false}>
          <Table<ResponseTarget>
            components={{
              body: { cell: EditableCell },
            }}
            scroll={{ x: "max-content" }}
            columns={mergedColumns}
            dataSource={canView ? data : []}
            loading={loading || loadingManage}
            rowKey={(record) => `${record._id}-${record.companyId}`}
          />
        </Form>
      </div>
    </div>
  );
};

export default TargetTable;
